import React from "react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { Section, SectionHeading, Eyebrow, Icon, PrimaryLink } from "@/components/common";
import { INDUSTRIES, BROCHURE, BG } from "@/data/content";
import { useLang } from "@/context/LanguageContext";

export default function Industries() {
  const { t } = useLang();
  return (
    <div>
      <Seo title="Industries" description="ERAM DIGITAL supports critical industries and enterprises with AI, cloud, industrial operations, software and managed services." path="/industries" />

      <Section className="py-20">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Industries</Eyebrow>
            <h1 className="font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl">Technology grounded in <span className="gradient-text">your world.</span></h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600">From asset-intensive operations to public services and consumer businesses, we connect technology to the work that matters.</p>
          </Reveal>
          <Reveal delay={0.1}><img src={BG.industries} alt="Connected energy, industrial and enterprise environments from the ERAM DIGITAL brochure" width="1536" height="1024" className="aspect-[4/3] w-full rounded-xl object-cover" data-testid="industries-graphic" /></Reveal>
        </div>
      </Section>
      <Section className="pb-20">
        <div className="grid gap-8 lg:grid-cols-3" data-testid="brochure-sectors">
          {BROCHURE.sectors.map((sector, i) => <div key={sector.id} className="border-t-2 border-brand-violet pt-6" data-testid={`sector-${sector.id}`}>
            <div className="flex items-center justify-between"><Icon name={sector.icon} className="h-8 w-8 text-brand-violet" /><span className="text-sm text-slate-400">0{i + 1}</span></div>
            <h2 className="mt-5 font-display text-lg font-bold">{sector.name}</h2>
            <ul className="mt-5 divide-y divide-slate-200">{sector.items.map((item) => <li key={item} className="py-3 text-sm text-slate-600">{item}</li>)}</ul>
          </div>)}
        </div>
      </Section>

      <Section className="pb-24">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind.name} delay={i * 0.06}>
              <div className="gradient-border group flex h-full flex-col rounded-2xl p-8 transition-transform hover:-translate-y-1" data-testid={`industry-card-${ind.name}`}>
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl gradient-bg text-white"><Icon name={ind.icon} className="h-7 w-7" /></div>
                <h3 className="font-display text-2xl font-semibold text-[#0A0A0A]">{ind.name}</h3>
                <p className="mt-3 flex-1 leading-relaxed text-[#4B5563]">{ind.value}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="pb-28">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-[#E5E7EB] bg-gradient-to-br from-brand-blue/10 via-brand-violet/10 to-brand-teal/10 p-10 text-center sm:p-14">
            <div className="relative z-10">
              <SectionHeading eyebrow="Your sector not listed?" title="We adapt to your" gradient="context." center subtitle="Tell us about your operations and we'll discuss where our capabilities can help." />
              <div className="mt-8 flex justify-center"><PrimaryLink to="/contact" testid="industries-cta">{t("cta.consult")}</PrimaryLink></div>
            </div>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
